import { Stone } from '@/game/Board';
import { t } from '@/texts';

// A number of 子 as Chinese counting says it, in quarters: 0, ¼, ½, ¾, 1, 1¼, …, 3¾.
export function ziText(n) {
  const whole = Math.floor(n + 1e-9);
  const quarter = ['', '¼', '½', '¾'][Math.round((n - whole) * 4) % 4];
  return whole === 0 && quarter ? quarter : `${whole}${quarter}`;
}

// The name of a handicap (让先, 让N子); empty for an even game.
export function handicapText(n) {
  if(n === 1) return t('handicap-first');
  return n > 1 ? t('handicap-stones', { n }) : '';
}

// The AI's levels in games against it, weakest first (plan 2026-10-28-ai-games.md, question 1),
// as the API names them.
export const aiLevels = ['20k', '15k', '12k', '10k', '8k', '6k', '5k', '4k', '3k', '2k', '1k', '1d', '2d', '3d', '4d', '5d', 'max'];

// A level's name: 5级, 3段, 最强.
export function aiLevelText(level) {
  if(level === 'max') return t('ai-level-max');
  const n = parseInt(level, 10);
  return t(level.endsWith('d') ? 'ai-level-dan' : 'ai-level-kyu', { n });
}

// The rule of a game, from a Game or a game as the API sends it: board size, komi (points) and the
// handicap (0 even, 1 让先, 2-9 stones).
function rule(game) {
  const setup = game.setup || game;
  const komi = typeof setup.komi === 'number' ? setup.komi : 7.5;
  return { grid: setup.grid || 19, komi, handicap: setup.handicap || 0 };
}

// What a counted game comes to under Chinese rules (SPEC §5.5): black's count in 子 (its points
// plus half of the shared ones), and the balance in points, black's points less white's, komi and
// the N points black returns with N handicap stones: above 0 black wins, below white, 0 a draw.
export function countFigures(game, result) {
  const { grid, komi, handicap } = rule(game);
  const returned = handicap >= 2 ? handicap : 0;
  const shared = grid * grid - result.black - result.white;
  return {
    black: result.black + shared / 2,
    balance: result.black - result.white - komi - returned,
    komi, handicap, returned,
  };
}

// The text confirming a count: the winner and the margin in 子, black's 子, and black's komi or
// return, neither for 让先 (SPEC §5.5).
export function countResultText(game, result) {
  const f = countFigures(game, result);
  const black = ziText(f.black);
  const color = t(f.balance > 0 ? 'color-black' : 'color-white');
  const margin = ziText(Math.abs(f.balance) / 2);
  // 让先: no komi and nothing to return, so only black's 子.
  if(f.handicap === 1) {
    if(f.balance === 0) return t('count-result-draw-first', { black });
    return t('count-result-score-first', { color, margin, black });
  }
  if(f.handicap > 0) {
    const n = ziText(f.returned / 2);
    if(f.balance === 0) return t('count-result-draw', { black, n });
    return t('count-result-score-handicap', { color, margin, black, n });
  }
  return t('count-result-score', { color, margin, black, komi: ziText(f.komi / 2) });
}

export default {
  stone(s) {
    let ss;

    if(typeof(s) === 'string')
      ss = Stone.parse(s);
    else
      ss = s;

    switch(ss) {
      case Stone.BLACK:
        return '黑';
      case Stone.WHITE:
        return '白';
      default:
        return null;
    }
  },

  stoneLong(s) {
    const t = this.stone(s);
    if(t)
      return `${t}棋`;
    else
      return null;
  },

  status(game) {
    if(!game) return null;

    switch(game.status) {
      case 'in-progress':
        if(game.nextPlayer)
          return this.stone(game.nextPlayer) + '下';
        else
          return '对局中';

      case 'completed':
        if(game.winner) {
          const color = this.stone(game.winner);
          if(game.countResult) {
            const margin = ziText(Math.abs(countFigures(game, game.countResult).balance) / 2);
            return t('status-win-by-count', { color, margin });
          }
          return t('status-win-by-resign', { color });
        } else {
          return t('status-draw');
        }
      default:
        return game.status;
    }
  },
}
